@if (isLoading$ | async) {
  <div style="padding:20px;color:var(--text-dim);text-align:center;">Loading<span class="pxl-dots"></span></div>
} @else {
<div class="settings-wrap">

  <!-- CELL SIZE + GRID LINE -->
  <div class="settings-group">
    <div class="row2">
      <div>
        <div class="pxl-section-label">Cell Size</div>
        <div class="cell-grid">
          @for (opt of cellSizeOptions; track opt.value) {
            <button class="cell-btn" [class.active]="selectedSetting.cellSize === opt.value"
              (click)="selectedSetting.cellSize = opt.value">
              <div class="cell-preview" [style.width.px]="opt.value + 2" [style.height.px]="opt.value + 2"></div>
              <span>{{ opt.value }}</span>
            </button>
          }
        </div>
      </div>
      <div>
        <div class="pxl-section-label">Grid Line Width</div>
        <div class="grid-line-opts">
          @for (opt of gridLineOptions; track opt.value) {
            <button class="cell-btn" [class.active]="selectedSetting.gridLineWidth === opt.value"
              (click)="selectedSetting.gridLineWidth = opt.value">
              <div class="grid-preview" [style.height.px]="Math.max(1, opt.value * 1.5)"></div>
              <span>{{ opt.label }}</span>
            </button>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- COLOR PALETTE -->
  <div class="settings-group">
    <div class="pxl-section-label">Color Palette</div>
    <div class="palette-count-row">
      <span class="pxl-field-label" style="margin:0;">Colors: <strong style="color:var(--accent);">{{ selectedSetting.paletteColorCount }}</strong></span>
      <input type="range" class="count-slider" [(ngModel)]="selectedSetting.paletteColorCount" min="1" max="64">
    </div>
    <div class="palette-picker-grid">
      @for (p of builtinPalettes; track p.id) {
        <button class="palette-card" [class.active]="selectedSetting.activePaletteId === p.id" (click)="selectPalette(p.id)">
          <div class="palette-card-swatches">
            @for (c of p.colors.slice(0,16); track $index) {
              <div class="mini-swatch" [style.background]="c"></div>
            }
          </div>
          <div class="palette-card-name">{{ p.name }}</div>
          <div class="palette-card-desc">{{ p.description }}</div>
        </button>
      }
    </div>
    @if (getActivePalette()) {
      <div class="active-palette-preview">
        <div class="pxl-field-label" style="margin-bottom:5px;">Active: {{ getActivePalette()?.name }}</div>
        <div class="preview-swatches">
          @for (c of getActivePaletteColors(); track $index) {
            <div class="preview-swatch" [style.background]="c" [title]="c"></div>
          }
        </div>
      </div>
    }
    <div class="custom-palettes-section">
      <div class="pxl-field-label" style="margin-top:8px;">Custom Palettes</div>
      @for (cp of selectedSetting.customPalettes; track cp.id) {
        <div class="custom-palette-row">
          <button class="palette-name-btn" [class.active]="selectedSetting.activePaletteId === cp.id"
            (click)="selectPalette(cp.id); editingCustomId = cp.id">{{ cp.name }} ({{ cp.colors.length }})</button>
          <button class="pxl-btn pxl-btn-danger pxl-btn-sm" (click)="deleteCustomPalette(cp.id)">✕</button>
        </div>
        @if (editingCustomId === cp.id) {
          <div class="custom-palette-editor">
            <div class="custom-swatches">
              @for (c of cp.colors; track $index) {
                <div class="custom-swatch-wrap">
                  <div class="custom-swatch" [style.background]="c"></div>
                  <button class="swatch-del" (click)="removeColorFromCustom(cp.id, $index)">×</button>
                </div>
              }
            </div>
            <div class="add-color-row">
              <input type="color" [(ngModel)]="newCustomColor" style="width:32px;height:28px;border:1px solid var(--border);border-radius:3px;background:none;cursor:pointer;padding:1px;">
              <button class="pxl-btn pxl-btn-ghost pxl-btn-sm" (click)="addColorToCustom(cp.id)">+ Add</button>
            </div>
          </div>
        }
      }
      <div class="add-palette-row mt6">
        <input type="text" class="pxl-input" [(ngModel)]="newPaletteName" placeholder="New palette name..." style="flex:1;">
        <button class="pxl-btn pxl-btn-secondary pxl-btn-sm" (click)="addCustomPalette()">+ Create</button>
      </div>
    </div>
  </div>

  <!-- INTERFACE (stored in IndexedDB with panel widths, not in project file) -->
  <div class="settings-group">
    <div class="pxl-section-label">Interface</div>
    <label class="settings-check-row">
      <input type="checkbox" [checked]="canvasHintsExpanded$ | async"
        (change)="onCanvasHintsExpandedChange($any($event.target).checked)">
      <span>Expand canvas shortcut hints by default</span>
    </label>
    <p class="settings-interface-hint">When off, only a chevron appears at the bottom-right of the canvas; click it to show Scroll, Fit, shortcuts, etc.</p>
  </div>

  <!-- THEME -->
  <div class="settings-group">
    <div class="pxl-section-label">Theme</div>
    <div class="theme-grid">
      @for (t of themes; track t.value) {
        <button class="theme-btn" [class.active]="selectedSetting.theme === t.value"
          (click)="selectedSetting.theme = t.value">{{ t.label }}</button>
      }
    </div>
  </div>

  <!-- ANIMATION SETTINGS -->
  <div class="settings-group">
    <div class="pxl-section-label">Animation</div>
    <div class="row2">
      <div>
        <label class="pxl-field-label">FPS</label>
        <div style="display:flex;flex-wrap:wrap;gap:3px;">
          @for (f of fpsOptions; track f) {
            <button class="cell-btn" [class.active]="selectedSetting.fps === f" (click)="selectedSetting.fps = f">{{ f }}</button>
          }
        </div>
      </div>
      <div>
        <label class="pxl-field-label">Undo History</label>
        <select class="pxl-input" [(ngModel)]="selectedSetting.undoHistoryDepth">
          @for (opt of undoDepthOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>
    </div>
  </div>
</div>
}
